"use client";
import { SingleProductQuery, SVariantDto } from "@/__generated__/sdk";
import { Button, Card, Chip, Divider, Fade, Grow, Icon, Stack, SxProps, Theme, Typography, useScrollTrigger } from "@mui/material";
import Box from "@mui/material/Box";
import _ from "lodash";
import ProductVariantsSectionColorsSelect from "@/app/(main)/products/[...slugs]/components/ProductVariantsSectionColorsSelect";
import React, { useCallback, useEffect, useState } from "react";
import ProductVariantsSectionSizesSelect from "@/app/(main)/products/[...slugs]/components/ProductVariantsSectionSizesSelect";
import ProductVariantsSectionMaterialsSelect from "@/app/(main)/products/[...slugs]/components/ProductVariantsSectionMaterialsSelect";
import { color } from "@mui/system";
import { ShoppingBasket, ShoppingCart, VerifiedUser } from "@mui/icons-material";
import ProductVariantAddToCartSection from "@/app/(main)/products/[...slugs]/components/ProductVariantAddToCartSection";
import { useCartActiveVariant, useCartItem } from "@/redux/features/cart/hooks";
import ShieldTickBoldIcon from "@/app/_components/Icons/ShieldTickBoldIcon";
import NoVariantsSection from "./NoVariantsSection";
import VariantPrice from "@/app/(main)/products/[...slugs]/components/VariantPrice";
import VariantStockAlert from "@/app/(main)/products/[...slugs]/components/VariantStockAlert";
import ProductVariantAddToCartSectionBottomBar from "@/app/(main)/products/[...slugs]/components/ProductVariantAddToCartSectionBottomBar";
import { useInView } from "react-intersection-observer";
type Props = {
  variants: SingleProductQuery["product"]["variants"];
  productId: string;
  sx?: SxProps<Theme> | undefined;
};
export default function ProductVariantsSection(props: Props) {
  const { ref, inView, entry } = useInView({
    threshold: 0.5,
    rootMargin: "0px 0px -200px 0px",
  });
  const cartActiveVariant = useCartActiveVariant(props.productId);
  const trigger = useScrollTrigger();

  if (props.variants.length <= 0) {
    return <NoVariantsSection />;
  }
  const colors = _.uniqBy(
    props.variants.flatMap((value) => value.color),
    (x) => x.id,
  );
  const materials = _.uniq(_.compact(props.variants.flatMap((a) => a.material)));
  const sizes = _.uniq(_.compact(props.variants.flatMap((a) => a.size)));
  const [selectedVariant, setSelectedVariant] = useState(() => {
    if (!cartActiveVariant) {
      return props.variants[0];
    }
    const variant = props.variants.find((v) => v.id === cartActiveVariant.variant.id);
    if (!variant) {
      return props.variants[0];
    }
    return variant;
  });

  const handleSizeChange = useCallback(
    (size: string | null) => {
      const foundVariant =
        props.variants.find((a) => a.size === size && a.color.id === selectedVariant.color.id && a.material === selectedVariant.material) ||
        props.variants.find((a) => a.size === size && a.color.id === selectedVariant.color.id) ||
        props.variants.find((a) => a.size === size && a.material === selectedVariant.material) ||
        props.variants.find((a) => a.size === size);
      if (foundVariant) setSelectedVariant(foundVariant);
    },
    [props.variants, selectedVariant],
  );

  const handleMaterialChange = useCallback(
    (material: string | null) => {
      const foundVariant =
        props.variants.find((a) => a.material === material && a.color.id === selectedVariant.color.id && a.size === selectedVariant.size) ||
        props.variants.find((a) => a.material === material && a.color.id === selectedVariant.color.id) ||
        props.variants.find((a) => a.material === material && a.size === selectedVariant.size) ||
        props.variants.find((a) => a.material === material);
      if (foundVariant) setSelectedVariant(foundVariant);
    },
    [props.variants, selectedVariant],
  );
  const handleColorChange = useCallback(
    (colorId: number) => {
      const foundVariant =
        props.variants.find((a) => a.color.id === colorId && a.material === selectedVariant.material && a.size === selectedVariant.size) ||
        props.variants.find((a) => a.color.id === colorId && a.material === selectedVariant.material) ||
        props.variants.find((a) => a.color.id === colorId && a.size === selectedVariant.size) ||
        props.variants.find((a) => a.color.id === colorId);
      if (foundVariant) setSelectedVariant(foundVariant);
    },
    [props.variants, selectedVariant],
  );

  const hasOffer = Boolean(selectedVariant.offer);
  const variantSimilarSizes = _.uniq(_.compact(props.variants.filter((a) => a.color.id === selectedVariant.color.id).map((a) => a.size)));
  const variantSimilarMaterial = _.uniq(
    _.compact(props.variants.filter((a) => a.color.id === selectedVariant.color.id).map((a) => a.material)),
  );
  const dashedSizes = _.difference(sizes, variantSimilarSizes);
  const dashedMaterials = _.difference(materials, variantSimilarMaterial);

  return (
    <>
      <Card
        elevation={0}
        sx={{
          boxShadow: { xs: "unset", md: "3px 6px 24px -6px #E8D9CF29, -3px 0px 24px -6px #E8D9CF1F" },
          backgroundColor: { xs: "transparent", md: "#fff" },
          borderRadius: 3,
          position: { xs: "relative", md: "sticky" },
          transition: "0.3s all",
          top: { md: trigger ? "88px" : "158px", xs: 0 },
          width: "100%",
          p: { xs: 0, md: 2 },
          pt: { xs: 6, md: 6 },
          ...props.sx,
        }}
      >
        <Fade in={hasOffer}>
          <Box
            sx={{
              position: "absolute",
              borderBottomRightRadius: 3 * 4,
              borderTopRightRadius: {xs:3*4, md:0},
              backgroundColor: "secondary.main",
              top: 0,
              color: "white.main",
              userSelect: "none",
              left: 0,
              p: hasOffer ? 0.8 : 0,
              px: { md: 2, xs: 1 },
            }}
          >
            <Typography variant={"subtitle1"}>{selectedVariant.offer?.offerType.title}</Typography>
          </Box>
        </Fade>
        <Stack sx={{ height: "100%" }} spacing={2}>
          <ProductVariantsSectionColorsSelect
            colors={colors}
            onChange={(id) => {
              handleColorChange(id);
            }}
            value={selectedVariant.color.id}
          />
          {sizes.length > 0 && (
            <ProductVariantsSectionSizesSelect
              sizes={sizes}
              dashed={dashedSizes}
              onChange={(title) => {
                handleSizeChange(title);
              }}
              value={selectedVariant.size}
            />
          )}
          {materials.length > 0 && (
            <ProductVariantsSectionMaterialsSelect
              materials={materials}
              dashed={dashedMaterials}
              onChange={(title) => {
                handleMaterialChange(title);
              }}
              value={selectedVariant.material}
            />
          )}
          <Divider sx={{ my: 3, display: { xs: "none", md: "block" } }} />
          <Card
            sx={{
              borderRadius: 3,
              boxShadow: { md: "unset", xs: "3px 6px 24px -6px #E8D9CF29, -3px 0px 24px -6px #E8D9CF1F" },
              backgroundColor: { md: "transparent", xs: "#fff" },
            }}
          >
            <Box sx={{ p: 1, display: "flex", alignItems: "center", justifyContent: "start", gap: 2 }}>
              <Icon sx={{ color: "secondary.main" }}>
                <ShieldTickBoldIcon />
              </Icon>
              <Typography variant={"body2"} color={"grey.800"}>
                {selectedVariant.guarantee.title}
              </Typography>
            </Box>
          </Card>
          <Box sx={{ marginTop: "auto !important" }}></Box>
          <Divider sx={{ my: 2, display: { xs: "none", md: "block" } }} />

          <VariantPrice variant={selectedVariant as SVariantDto} />
          <VariantStockAlert variant={selectedVariant as SVariantDto} />
          <ProductVariantAddToCartSection variant={selectedVariant as SVariantDto} productId={props.productId} />
        </Stack>
      </Card>
    </>
  );
}
